import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePos } from '../context/PosContext';
import { User, Role } from '../types';
import * as db from '../services/db';
import { idbGetRaw, idbSetRaw } from '../services/idb';
import { generateQrMatrix } from '../services/qrcode';
import './QrLogin.css';

// QR Login — local-first reconstruction of qrcode_login_fragment.xml.
// Android shows a device card (outlet identity + subscription/network status)
// beside a QR code that the cloud portal scans to pair/authorise the device,
// with a Quick-PIN keypad as the alternative login. There is no pairing
// backend in the web port, so the QR encodes this device's identity (a real,
// scannable pairing URL drawn locally) while the **Quick PIN** keypad is fully
// functional: it signs an operator in through the existing Users system
// (db currentUser), exactly as the rest of the app already expects.

const APP_VERSION = '3.5.36 (web)';
const COMPANY = 'www.eposdigital.com';
const SUPPORT_NO = '+91 7738337321/8828238154/9167227341';

// One stable device id per browser, created on first visit (Android serialNo).
async function getDeviceId(): Promise<string> {
  const KEY = 'sconnect.deviceId';
  let id = await idbGetRaw(KEY);
  if (!id) {
    const rnd = () => Math.random().toString(36).slice(2, 6).toUpperCase();
    id = `SC-${rnd()}-${rnd()}`;
    await idbSetRaw(KEY, id);
  }
  return id;
}

// Subscription renewal date — seeded to one year from first run, mirroring the
// Android "renewal date" warning. Stored locally; editable on the About screen.
async function getRenewalDate(): Promise<Date> {
  const KEY = 'sconnect.renewalDate';
  let iso = await idbGetRaw(KEY);
  if (!iso) {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    iso = d.toISOString();
    await idbSetRaw(KEY, iso);
  }
  return new Date(iso);
}

const QrLogin: React.FC = () => {
  const navigate = useNavigate();
  const { config } = usePos();

  const [now, setNow] = useState(new Date());
  const [online, setOnline] = useState(navigator.onLine);
  const [mode, setMode] = useState<'qr' | 'pin'>('qr');
  const [nonce, setNonce] = useState(() => Math.floor(100000 + Math.random() * 899999));

  // operator / quick-pin state
  const [users, setUsers] = useState<User[]>([]);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [selUserId, setSelUserId] = useState<string>('');
  const [roles, setRoles] = useState<Role[]>([]);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState(false);

  const [deviceId, setDeviceId] = useState('');
  const [renewal, setRenewal] = useState<Date>(() => new Date());

  // Load operators, the current login, roles and the local device/renewal info.
  const load = useCallback(async () => {
    const [all, cid, r] = await Promise.all([db.getUsers(), db.getCurrentUserId(), db.getRoles()]);
    const active = all.filter((u) => u.active);
    setUsers(active);
    setCurrentId(cid);
    setSelUserId((prev) => prev || active[0]?.id || '');
    setRoles(r);
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    getDeviceId().then(setDeviceId);
    getRenewalDate().then(setRenewal);
  }, []);

  // live clock
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // network status
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  const currentUser = currentId ? users.find((u) => u.id === currentId) ?? null : null;

  // Resolve a role id to its display name (custom roles have opaque ids).
  const roleLabel = (id: string) => roles.find((r) => r.id === id)?.name ?? id;

  // ---- subscription warning ----
  const daysLeft = Math.ceil((renewal.getTime() - now.getTime()) / 86400000);
  const subText =
    daysLeft < 0
      ? `Subscription expired on ${renewal.toLocaleDateString()}`
      : daysLeft <= 30
      ? `Renewal due in ${daysLeft} day${daysLeft === 1 ? '' : 's'} (${renewal.toLocaleDateString()})`
      : `Subscription valid till ${renewal.toLocaleDateString()}`;
  const subWarn = daysLeft <= 30;

  // ---- QR payload + matrix ----
  const qrPayload = useMemo(
    () =>
      `https://app.eposdigital.com/pair?d=${encodeURIComponent(deviceId)}` +
      `&o=${encodeURIComponent(config.storeName || 'Outlet')}&n=${nonce}`,
    [deviceId, config.storeName, nonce]
  );
  const matrix = useMemo(() => generateQrMatrix(qrPayload, 'M'), [qrPayload]);

  const refresh = useCallback(() => {
    setNonce(Math.floor(100000 + Math.random() * 899999));
    load();
  }, [load]);

  // ---- quick pin login ----
  const pressKey = (d: string) => {
    setPinError(false);
    setPin((p) => (p.length >= 6 ? p : p + d));
  };
  const backspace = () => {
    setPinError(false);
    setPin((p) => p.slice(0, -1));
  };

  const doLogin = () => {
    const user = users.find((u) => u.id === selUserId);
    if (!user) return alert('Select a user first.');
    if (pin.length < 4) {
      setPinError(true);
      return;
    }
    if (pin !== user.pin) {
      setPinError(true);
      setPin('');
      return;
    }
    db.setCurrentUserId(user.id);
    db.logEvent({ type: 'login', detail: `Logged in as ${user.name} (Quick PIN)`, userName: user.name });
    setCurrentId(user.id);
    setPin('');
    setPinError(false);
  };

  const logout = () => {
    if (!currentUser) return;
    db.logEvent({ type: 'login', detail: `Logged out ${currentUser.name}`, userName: currentUser.name });
    db.setCurrentUserId(null);
    setCurrentId(null);
  };

  return (
    <div className="ql-page">
      <div className="ql-clock">{now.toLocaleString(undefined, {
        month: 'short', day: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      })}</div>

      <div className="ql-card">
        {/* ---- left: outlet identity ---- */}
        <div className="ql-left">
          <div className="ql-left-actions">
            <button className="ql-icon-btn" title="Refresh" onClick={refresh}>⟳</button>
            <button className="ql-icon-btn" title="Settings" onClick={() => navigate('/settings')}>⚙</button>
            <button
              className="ql-icon-btn"
              title={mode === 'qr' ? 'Quick PIN login' : 'Show QR'}
              onClick={() => setMode((m) => (m === 'qr' ? 'pin' : 'qr'))}
            >
              {mode === 'qr' ? '⌨' : '▦'}
            </button>
            {currentUser && (
              <button className="ql-icon-btn ql-icon-logout" title={`Log out ${currentUser.name}`} onClick={logout}>⏻</button>
            )}
          </div>

          <div className="ql-logo">SC</div>
          <div className="ql-outlet">{config.storeName || 'Your Outlet'}</div>
          {config.address && <div className="ql-branch">{config.address}</div>}

          <div className={`ql-sub ${subWarn ? 'warn' : ''}`}>{subText}</div>

          <div className={`ql-net ${online ? 'ok' : 'down'}`}>
            <span className="ql-dot" /> {online ? 'Online' : 'Offline — check your connection'}
          </div>

          {currentUser ? (
            <div className="ql-operator">Operator: <strong>{currentUser.name}</strong> · {roleLabel(currentUser.role)}</div>
          ) : (
            <div className="ql-operator muted">No operator logged in</div>
          )}

          <div className="ql-meta">
            <span>{COMPANY}</span>
            <span>v{APP_VERSION}</span>
          </div>
        </div>

        {/* ---- right: QR pairing OR quick-pin pad ---- */}
        <div className="ql-right">
          {mode === 'qr' ? (
            <div className="ql-qr-wrap">
              <QrSvg matrix={matrix} />
              <div className="ql-serial">{deviceId}</div>
              <div className="ql-qr-hint">Scan from the eposdigital solutionweb portal to pair this device.</div>
            </div>
          ) : (
            <div className="ql-pin">
              <div className="ql-pin-title">Quick PIN Login</div>

              <select
                className="ql-user-select"
                value={selUserId}
                onChange={(e) => { setSelUserId(e.target.value); setPin(''); setPinError(false); }}
              >
                {users.length === 0 && <option value="">No active users — add one in Users</option>}
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name} ({roleLabel(u.role)})</option>
                ))}
              </select>

              <div className={`ql-pin-display ${pinError ? 'err' : ''}`}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <span key={i} className={`ql-pin-dot ${i < pin.length ? 'on' : ''}`} />
                ))}
              </div>
              {pinError && <div className="ql-pin-err">Wrong PIN — try again</div>}

              <div className="ql-keypad">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((k) => (
                  <button key={k} className="ql-key" onClick={() => pressKey(k)}>{k}</button>
                ))}
                <button className="ql-key ql-key-fn" onClick={backspace}>⌫</button>
                <button className="ql-key" onClick={() => pressKey('0')}>0</button>
                <button className="ql-key ql-key-enter" onClick={doLogin}>↵</button>
              </div>

              <p className="ql-pin-foot muted">
                Manage operators &amp; PINs in <button className="ql-link" onClick={() => navigate('/users')}>Users</button>.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="ql-footer">
        <span>{config.storeName}{config.address ? ` · ${config.address}` : ''}</span>
        <span>For support call us: {SUPPORT_NO}</span>
      </div>
    </div>
  );
};

// Render a QR boolean matrix as a crisp, scalable SVG with a quiet zone.
const QrSvg: React.FC<{ matrix: boolean[][] }> = ({ matrix }) => {
  const n = matrix.length;
  const quiet = 4;
  const dim = n + quiet * 2;
  const rects: string[] = [];
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      if (matrix[y][x]) rects.push(`M${x + quiet},${y + quiet}h1v1h-1z`);
    }
  }
  return (
    <svg className="ql-qr" viewBox={`0 0 ${dim} ${dim}`} shapeRendering="crispEdges" role="img" aria-label="Device pairing QR code">
      <rect width={dim} height={dim} fill="#ffffff" />
      <path d={rects.join('')} fill="#000000" />
    </svg>
  );
};

export default QrLogin;
